import { z } from "zod";

import { CampaignDisplayStatus } from "@signalist/shared-types";

/**
 * `POST /reports/overview` — one row per list, expandable into its campaigns.
 *
 * Rates arrive pre-computed and nullable: `null` means "no denominator yet", which the
 * table renders as "—". A 0% would claim we contacted people and nobody replied.
 */

const rate = z.coerce.number().nullable();

export const OverviewMetricsSchema = z.object({
	assignedLeads: z.coerce.number(),
	contactedLeads: z.coerce.number(),
	endedCampaignLeads: z.coerce.number(),
	repliedLeads: z.coerce.number(),
	positiveRepliedLeads: z.coerce.number(),
	oooRepliedLeads: z.coerce.number(),

	contactedRate: rate,
	endedRate: rate,
	replyRate: rate,
	positiveRate: rate,
	sentimentRatio: rate,

	linkedinInviteSent: z.coerce.number(),
	linkedinAcceptRate: rate,
	linkedinReplyRate: rate,
	linkedinPositiveRate: rate,

	emailsSendUnique: z.coerce.number(),
	emailOpenRate: rate,
	emailClickRate: rate,
	emailReplyRate: rate,
	emailPositiveRate: rate,
	emailBounceRate: rate,
});

export type OverviewMetrics = z.infer<typeof OverviewMetricsSchema>;

export const OverviewChannelSchema = z.enum(["linkedin", "email"]);
export type OverviewChannel = z.infer<typeof OverviewChannelSchema>;

export const OverviewSenderSchema = z.object({
	name: z.string(),
	initials: z.string(),
});

export type OverviewSender = z.infer<typeof OverviewSenderSchema>;

export const OverviewLastActivitySchema = z.object({
	at: z.coerce.date(),
	kind: z.string(),
});

export type OverviewLastActivity = z.infer<typeof OverviewLastActivitySchema>;

export const OverviewCampaignRowSchema = z.object({
	campaignId: z.string(),
	name: z.string(),
	status: z.nativeEnum(CampaignDisplayStatus).optional(),
	locked: z.boolean(),
	channels: z.array(OverviewChannelSchema),
	senders: z.array(OverviewSenderSchema),
	lastActivity: OverviewLastActivitySchema.nullable(),
	metrics: OverviewMetricsSchema,
});

export type OverviewCampaignRow = z.infer<typeof OverviewCampaignRowSchema>;

export const OverviewListRowSchema = z.object({
	listId: z.number(),
	listName: z.string(),
	channels: z.array(OverviewChannelSchema),
	senders: z.array(OverviewSenderSchema),
	lastActivity: OverviewLastActivitySchema.nullable(),
	metrics: OverviewMetricsSchema,
	campaigns: z.array(OverviewCampaignRowSchema),
});

export type OverviewListRow = z.infer<typeof OverviewListRowSchema>;

export const OverviewHighlightsSchema = z.object({
	activeCampaigns: z.coerce.number(),
	totalCampaigns: z.coerce.number(),
	currentlyAssignedLeads: z.coerce.number(),
	assignedLeadsInPeriod: z.coerce.number(),
});

export type OverviewHighlights = z.infer<typeof OverviewHighlightsSchema>;

export const AnalyticsOverviewSchema = z.object({
	highlights: OverviewHighlightsSchema,
	rows: z.array(OverviewListRowSchema),
});

export type AnalyticsOverview = z.infer<typeof AnalyticsOverviewSchema>;

/** `POST /reports/top-campaigns` — the company's best campaigns, all time. */
export const RankedCampaignSchema = z.object({
	listId: z.number(),
	listName: z.string(),
	campaignId: z.string(),
	campaignName: z.string(),
	assignedLeads: z.coerce.number(),
	contactedLeads: z.coerce.number(),
	replyRate: rate,
	positiveRate: rate,
	sentimentRatio: rate,
});

export type RankedCampaign = z.infer<typeof RankedCampaignSchema>;

export const TopCampaignsSchema = z.object({
	lifetime: z.object({
		assignedLeads: z.coerce.number(),
		contactedLeads: z.coerce.number(),
		replyRate: rate,
		positiveRate: rate,
		sentimentRatio: rate,
	}),
	campaigns: z.array(RankedCampaignSchema),
	/** The volume floor the API applied — shown so the ranking is not mistaken for "everything". */
	minContactedLeads: z.coerce.number(),
});

export type TopCampaigns = z.infer<typeof TopCampaignsSchema>;
